import { useRef } from "react";
import { motion, useScroll } from "motion/react";
import {
  Check,
  Scan,
  ShoppingCart,
  Star,
} from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { RotatingPhone } from "@/landing/components/RotatingPhone";

const STEP_ICONS = [Scan, Star, Check, ShoppingCart] as const;

export function HowItWorksSection() {
  const { messages } = useI18n();
  const hiw = messages.howItWorks;
  const containerRef = useRef<HTMLElement | null>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  return (
    <section
      id="how-it-works"
      ref={containerRef}
      className="relative bg-white"
    >
      <div className="sticky top-0 flex min-h-screen items-center py-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid items-center gap-16 lg:grid-cols-2">
            <div className="sticky top-20 hidden items-center justify-center lg:flex">
              <RotatingPhone scrollYProgress={scrollYProgress} />
            </div>

            <div className="space-y-24">
              <div className="mb-16">
                <h2 className="mb-6 text-4xl text-[#111111] lg:text-6xl">
                  {hiw.heading}
                </h2>
                <p className="text-xl text-gray-600">{hiw.subheading}</p>
              </div>

              {hiw.steps.map((step, index) => {
                const Icon = STEP_ICONS[index];
                if (!Icon) return null;
                return (
                  <motion.div
                    key={step.title}
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="relative"
                  >
                    <div className="flex items-start gap-6">
                      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-[#4CAF50]">
                        <Icon className="h-8 w-8 text-white" />
                      </div>
                      <div>
                        <div className="mb-2 text-sm text-[#4CAF50]">
                          {hiw.stepPrefix} {index + 1}
                        </div>
                        <h3 className="mb-3 text-2xl text-[#111111] lg:text-3xl">
                          {step.title}
                        </h3>
                        <p className="text-lg text-gray-600">
                          {step.description}
                        </p>
                      </div>
                    </div>

                    {index < hiw.steps.length - 1 ? (
                      <div className="ml-8 mt-6 mb-6 h-12 w-0.5 bg-gray-200" />
                    ) : null}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
